import React, { useState } from 'react';
import { UspsRuleRow, STATUS_LABEL } from './UspsRuleRow';
import type { RuleStatus, UspsRule, UspsVettingReport } from './uspsVetting.types';

/** The rule lists shared by the carrier-screen panel (by result) and the full tab (by USPS section). */

type Filter = 'ALL' | RuleStatus;

const FILTERS: Array<{ id: Filter; label: string }> = [
  { id: 'ALL', label: 'All' },
  { id: 'FAIL', label: STATUS_LABEL.FAIL },
  { id: 'REVIEW', label: STATUS_LABEL.REVIEW },
  { id: 'NOT_EVALUATED', label: STATUS_LABEL.NOT_EVALUATED },
  { id: 'PASS', label: STATUS_LABEL.PASS },
];

export const allRules = (report: UspsVettingReport): UspsRule[] => report.groups.flatMap((g) => g.rules);

/** Rules grouped under the USPS document's own sections, with filter chips. */
export const UspsGroupedRules: React.FC<{ report: UspsVettingReport }> = ({ report }) => {
  const [filter, setFilter] = useState<Filter>('ALL');
  const s = report.summary;
  const counts: Record<Filter, number> = { ALL: s.total, FAIL: s.fail, REVIEW: s.review, NOT_EVALUATED: s.notEvaluated, PASS: s.pass };
  return (
    <>
      <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter rules by result">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            onClick={() => setFilter(f.id)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${filter === f.id ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
          >
            {f.label} <span className="tabular-nums opacity-70">{counts[f.id]}</span>
          </button>
        ))}
      </div>
      {report.groups.map((g) => {
        const rules = filter === 'ALL' ? g.rules : g.rules.filter((r) => r.status === filter);
        if (rules.length === 0) return null;
        return (
          <section key={g.id}>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-widest text-gray-500">{g.title}</h3>
            <ul>
              {rules.map((r) => (
                <UspsRuleRow key={r.id} rule={r} />
              ))}
            </ul>
          </section>
        );
      })}
    </>
  );
};

const SECTIONS: Array<{ status: RuleStatus; title: string; hint: string; open: boolean }> = [
  { status: 'FAIL', title: 'Not matching USPS rules', hint: 'This carrier breaks these rules.', open: true },
  { status: 'REVIEW', title: 'Needs a manual check', hint: 'Not a failure, but our data cannot confirm it.', open: true },
  { status: 'PASS', title: 'Matching USPS rules', hint: 'This carrier meets these rules.', open: true },
  { status: 'NOT_EVALUATED', title: 'Could not be checked (no data)', hint: 'Never counted as a pass.', open: false },
];

/** The same rules sorted by result: what does not match first, then what matches. */
export const UspsStatusSections: React.FC<{ report: UspsVettingReport }> = ({ report }) => {
  const rules = allRules(report);
  return (
    <div className="space-y-4">
      {SECTIONS.map((sec) => {
        const items = rules.filter((r) => r.status === sec.status);
        if (items.length === 0) return null;
        return (
          <details key={sec.status} open={sec.open} className="group">
            <summary className="cursor-pointer select-none text-sm font-semibold text-gray-900">
              {sec.title} <span className="tabular-nums text-gray-500">({items.length})</span>
              <span className="ml-2 text-xs font-normal text-gray-500">{sec.hint}</span>
            </summary>
            <ul className="mt-1">
              {items.map((r) => (
                <UspsRuleRow key={r.id} rule={r} />
              ))}
            </ul>
          </details>
        );
      })}
    </div>
  );
};
